<template>
  <div class="responses-list">
    <v-table
      class="vcv-responses-table"
      :headers="headers"
      :items="list"
      :filter="filter"
      :table-name="tableName"
      :loading="loading"
      @updateHeaders="updateHeaders"
      @setFilters="setFilters"
      @cancel_filter="cancel_filter"
    >
      <template slot="action-buttons">
        <div v-on-click-away="hideSettingsDropdown" class="table-settings_wrapper table-icon_wrapper">
          <div
            v-tooltip.left="$tr('invites.exportToExcel')"
            class="table-settings table-icon"
            @click="toggleSettingsDropdown"
          >
            <!-- <svg class="cv-row-icon"><use xlink:href="/static/icons/table-icons.svg#export" /></svg> -->
            <i class="icon icon-export"></i>
          </div>

          <transition name="fade">
            <div v-if="isSettingsDropdownVisible" class="table-settings_dropdown">
              <a class="table-settings_item" @click="e => exportResponses(e, 'last')">
                {{ $tr('exportToExcel.last') }}
              </a>

              <a
                v-tooltip.top="$tr('exportToExcel.filteredTooltip')"
                class="table-settings_item"
                :class="{ disabled: filter_query.length === 0 }"
                @click="e => exportResponses(e, 'filtered')"
              >
                {{ $tr('exportToExcel.filtered') }}
              </a>

              <a
                v-tooltip.top="$tr('exportToExcel.allTooltip')"
                class="table-settings_item"
                @click="e => exportResponses(e, 'all')"
              >
                {{ $tr('exportToExcel.allResponses') }}
              </a>
            </div>
          </transition>
        </div>
      </template>

      <template #header="{ header }">
        <table-head :header="header" v-bind="{ ...$attrs, ...$props }" v-on="$listeners" />
      </template>

      <template #cell="{ cell, header }">
        <response-row
          :model="cell"
          :header="header"
          :cell="cell"
          :statuses="statuses"
          :loading-status="loadingStatus"
          @statusChange="$emit('status_change', $event)"
        />
      </template>
    </v-table>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import PROCESS_NAMES from '@/process/names';
import Moment from 'moment';
import { directive as VueClickAway } from 'vue-clickaway';
import TableHead from './tablehead/TableHead';
import ResponseRow from './Row';
import VTable from '@/components/table/index.vue';
import Axios from 'axios';

export default {
  name: 'ResponsesList',

  components: {
    TableHead,
    ResponseRow,
    VTable
  },

  directives: {
    onClickAway: VueClickAway
  },

  props: {
    list: Array,
    loading: Boolean,
    loadingStatus: Boolean
  },

  data() {
    return {
      tableName: 'responses',
      headers: [
        {
          id: 'checkbox',
          name: '',
          is_visible: true,
          is_base: true,
          width: '60px'
        },
        {
          id: 'avatar',
          name: this.$tr('common.list.candidate'),
          is_visible: true,
          is_base: false,
          width: '215px'
        },
        {
          id: 'vacancy',
          name: this.$tr('common.list.vacancy'),
          is_visible: true,
          is_base: false,
          width: ''
        },
        {
          id: 'status',
          name: this.$tr('common.list.status'),
          is_visible: true,
          is_base: false,
          width: ''
        },
        {
          id: 'refuse',
          name: this.$tr('common.list.refusalReason'),
          is_visible: true,
          is_base: false,
          width: ''
        },
        {
          id: 'rating',
          name: this.$tr('common.list.rating'),
          is_visible: true,
          is_base: false,
          width: '150px'
        },
        {
          id: 'date',
          name: this.$tr('common.list.created'),
          is_visible: false,
          is_base: false,
          width: ''
        },
        {
          id: 'date_sent',
          name: this.$tr('common.list.date'),
          is_visible: true,
          is_base: false,
          width: ''
        },
        {
          id: 'source',
          name: this.$tr('common.list.source'),
          is_visible: true,
          is_base: false,
          width: ''
        },
        {
          id: 'creator',
          name: this.$tr('common.list.vacancyCreator'),
          is_visible: true,
          is_base: false,
          width: ''
        },
        {
          id: 'tags',
          name: this.$tr('common.list.tags'),
          is_visible: true,
          is_base: false,
          width: ''
        },
        {
          id: 'resume',
          name: this.$tr('common.list.resume'),
          is_visible: true,
          is_base: false,
          width: ''
        },
        {
          id: 'comments',
          name: this.$tr('common.list.comments'),
          is_visible: true,
          is_base: false,
          width: ''
        }
      ],

      additionalHeaders: [],
      isSettingsDropdownVisible: false
    };
  },

  computed: {
    ...mapState('responses', ['filter', 'xlsExporting']),

    ...mapGetters('responses', ['filter_query', 'selectedList', 'xls_export_params']),

    ...mapGetters('process', ['getProcessName']),

    statuses() {
      return this.$store.getters['dictionary/responseStatuses'].filter(i => !i.is_system);
    }
  },

  async created() {
    await this.getAttributes();
  },

  methods: {
    toggleSettingsDropdown() {
      this.isSettingsDropdownVisible = !this.isSettingsDropdownVisible;
    },

    hideSettingsDropdown() {
      this.isSettingsDropdownVisible = false;
    },

    cancel_filter() {
      this.$emit('status_clear');
      this.$emit('cancel_filter');
    },

    setFilters(val) {
      this.$nextTick().then(() => {
        setTimeout(() => {
          this.$store.commit('responses/setFilter', val);
          this.$emit('fetch');
        }, 0);
      });
    },

    async updateHeaders(headers) {
      this.headers = headers;
      await this.getAttributes();
      // this.headers.splice(0, this.headers.length, ...headers);
      // this.headers.concat(this.additionalHeaders);
    },

    async getAttributes() {
      this.additionalHeaders = await Axios.get('/v3/response-attributes').then(res => {
        return res.data._embedded.attributes.map(item => {
          return {
            id: `attribute_${item.code}`,
            name: item.title,
            is_visible: false,
            is_base: false,
            width: ''
          };
        });
      });

      this.headers = [...this.headers, ...this.additionalHeaders];
    },

    exportResponses(e, type = 'all') {
      if (e.target.className.indexOf('disabled') !== -1 || this.xlsExporting) {
        return false;
      }
      let filter = null;

      if (type === 'filtered') {
        filter = this.filter_query;
      }
      if (type === 'last') {
        let today = Moment().format('YYYY-MM-DD');
        let previous_date = Moment(today).subtract(30, 'days').format('YYYY-MM-DD');
        filter = [
          {
            id: 'date_sent',
            list: [previous_date, today]
          }
        ];
      }

      const params = {
        ids: this.selectedList.map(i => i.id),
        filter
      };

      // process
      if (this.getProcessName === PROCESS_NAMES.DANONE) {
        const responseIds = this.selectedList ? this.selectedList.map(response => response.id) : [];

        Axios.get(`/1.0/spec-private/danone/responses/report/xls`, {
          params: this.xls_export_params(responseIds)
        })
          .then(response => {
            window.location = response.data.data[0].url;
          })
          .catch(() => {
            this.$noty.error(this.$tr('vacancy.preferences.modals.saveFailure.text'));
          });
        // process end
      } else {
        this.$store.dispatch('responses/xlsExport', params);
        this.$noty.success(this.$tr('responses.exportToExcelSuccess'));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
  .vtable_wrapper .table-settings_dropdown {
    padding: 12px;
    min-width: 208px;
  }
  .table-settings_item {
    white-space: nowrap;
    line-height: 24px;
    font-size: 15px;
  }
  .vcv-responses-table {
    height: 100%;
    position: relative;
    z-index: 1;

    ::v-deep {
      th {
        position: relative;
        z-index: 1;
      }
    }
  }

  .table-icon {
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    color: #9faab6;

    svg {
      max-width: 100%;
      max-height: 100%;
    }
  }

  .responses-list {
    position: relative;
  }
</style>
